/**
 * Filter Group Processor - Processes groups of filters with logical operators
 *
 * Handles processing of filter groups (AND/OR combinations) and combines
 * the results into unified filter and relationship structures.
 *
 * @module queryBuilderCore/processors/filterGroupProcessor
 */

import {
  FilterGroup,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';

import { processFilter } from './filterProcessor';

/**
 * Processes a filter group and combines filters and relationships
 *
 * @param group - Filter group to process
 * @param entityType - Type of entity being filtered
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param technologyFilterOptions - Available technology filter options
 * @param seedFilterOptions - Available seed filter options
 * @param integrationValues - List of integration source values
 * @param queryContentTab - Current query content tab context
 * @returns Combined direct filters, relationships, and AD object types
 */
export function processFilterGroup(
  group: FilterGroup,
  entityType: string,
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  technologyFilterOptions?: Record<string, unknown>,
  seedFilterOptions?: Record<string, unknown>,
  integrationValues?: string[],
  _queryContentTab?: string
): {
  directFilters: QueryFilter[];
  relationships: Relationship[];
} {
  const directFilters: QueryFilter[] = [];
  const relationships: Relationship[] = [];

  group.filters.forEach(filter => {
    const result = processFilter(
      filter,
      entityType,
      group.logicalOperator,
      assetFilterOptions,
      vulnFilterOptions,
      technologyFilterOptions,
      seedFilterOptions,
      integrationValues
    );

    if (result.directFilter) {
      if (result.directFilter.not === undefined) {
        result.directFilter.not = result.isNot || false;
      }
      directFilters.push(result.directFilter);
    }

    if (result.relationships) {
      result.relationships.forEach(rel => {
        if (rel.target?.filters) {
          rel.target.filters.forEach(f => {
            if (f.field === 'value') {
              f.not = result.isNot || false;
            }
          });
        }
      });
      relationships.push(...result.relationships);
    }
  });

  const combinedDirectFilters: QueryFilter[] = [];

  if (directFilters.length > 0) {
    if (group.logicalOperator === 'OR' && directFilters.length > 1) {
      combinedDirectFilters.push({
        field: '',
        operator: 'OR',
        value: directFilters,
      });
    } else {
      combinedDirectFilters.push(...directFilters);
    }
  }

  const combinedRelationships: Relationship[] = [];

  if (group.logicalOperator === 'OR' && relationships.length > 1) {
    // Separate HAS_PORT relationships (need special OR handling)
    const portRels = relationships.filter(r => r.label === 'HAS_PORT');
    const attributeRels = relationships.filter(
      r => r.label === 'HAS_ATTRIBUTE'
    );
    const otherRels = relationships.filter(
      r => r.label !== 'HAS_ATTRIBUTE' && r.label !== 'HAS_PORT'
    );

    // HAS_PORT relationships from "is any of" operator should ALWAYS use OR logic
    // regardless of filter group operator, because "is any of" means "match ANY value"
    if (portRels.length > 1) {
      const orValue = portRels.map(rel => ({
        field: '',
        operator: 'AND',
        value: rel.target?.filters || [],
      }));

      const combinedPortRel: Relationship = {
        label: 'HAS_PORT',
        // CRITICAL: Do NOT use select_one when we want ALL matching ports
        // select_one: true would return only the first matching port per asset
        // We need ALL ports that match the filter to be returned
        target: {
          labels: ['Port'],
          filters: [{ field: '', operator: 'OR', value: orValue }],
        },
      };

      combinedRelationships.push(combinedPortRel);
    } else if (portRels.length === 1) {
      // For single port filter, keep as-is from original relationship
      combinedRelationships.push(portRels[0]);
    }

    // HAS_ATTRIBUTE relationships follow filter group operator
    if (attributeRels.length > 1) {
      const orValue = attributeRels.map(rel => ({
        field: '',
        operator: 'AND',
        value: rel.target?.filters || [],
      }));

      const combinedAttributeRel: Relationship = {
        label: 'HAS_ATTRIBUTE',
        select_one: true,
        target: {
          labels: ['Attribute'],
          filters: [{ field: '', operator: 'OR', value: orValue }],
        },
      };
      combinedRelationships.push(combinedAttributeRel);
    } else if (attributeRels.length === 1) {
      combinedRelationships.push(attributeRels[0]);
    }

    combinedRelationships.push(...otherRels);
  } else {
    combinedRelationships.push(...relationships);
  }

  return {
    directFilters: combinedDirectFilters,
    relationships: combinedRelationships,
  };
}
